import {
  canvasToBlob,
  ConvertedWebpImage,
  loadImageElement,
  slugifyOriginalName,
  toSeoWebpFilename,
} from './wordpressMedia.service';

export const DEFAULT_BRAND_LOGO_SRC = '/images/brand/keystone-overlay.svg';
export const OVERLAY_PRIMARY_LABEL_ID = 'primary-label';
export const OVERLAY_SECONDARY_LABEL_ID = 'secondary-label';
export const DEFAULT_OVERLAY_PRIMARY_LABEL = 'ROSEWOOD';
export const DEFAULT_OVERLAY_SECONDARY_LABEL = 'REHAU uPVC FRENCH DOORS';

export const OVERLAY_SLOTS = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'middle-center',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
] as const;
export type OverlaySlot = (typeof OVERLAY_SLOTS)[number];

export const OVERLAY_GRID: OverlaySlot[][] = [
  ['top-left', 'top-center', 'top-right'],
  ['middle-left', 'middle-center', 'middle-right'],
  ['bottom-left', 'bottom-center', 'bottom-right'],
];

export type OverlayPlacement = {
  position: OverlaySlot;
  primaryLabel: string;
  secondaryLabel: string;
};

export const DEFAULT_OVERLAY_PLACEMENT: OverlayPlacement = {
  position: 'bottom-right',
  primaryLabel: DEFAULT_OVERLAY_PRIMARY_LABEL,
  secondaryLabel: DEFAULT_OVERLAY_SECONDARY_LABEL,
};

export type OverlayBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function isOverlaySlot(value: unknown): value is OverlaySlot {
  return OVERLAY_SLOTS.includes(String(value || '') as OverlaySlot);
}

export function overlaySlotRow(slot: OverlaySlot): 'top' | 'middle' | 'bottom' {
  if (slot.startsWith('top')) return 'top';
  if (slot.startsWith('bottom')) return 'bottom';
  return 'middle';
}

export function overlaySlotCol(slot: OverlaySlot): 'left' | 'center' | 'right' {
  if (slot.endsWith('left')) return 'left';
  if (slot.endsWith('right')) return 'right';
  return 'center';
}

export function overlaySlotLabel(slot: OverlaySlot) {
  const row = overlaySlotRow(slot);
  const col = overlaySlotCol(slot);
  const rowLabel = row === 'middle' ? 'Middle' : row[0].toUpperCase() + row.slice(1);
  const colLabel = col === 'center' ? 'centre' : col;
  return `${rowLabel} ${colLabel}`;
}

export function normalizeOverlayLabel(value: string, fallback = '') {
  return String(value || '').replace(/\s+/g, ' ').trim() || fallback;
}

function escapeXml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function replaceLabeledText(svgText: string, id: string, value: string) {
  const next = escapeXml(value);
  const source = String(svgText || '');
  const withText = source.replace(
    new RegExp(`(<(?:text|tspan)\\b[^>]*\\bid=["']${id}["'][^>]*>)([\\s\\S]*?)(<\\/(?:text|tspan)>)`, 'i'),
    `$1${next}$3`
  );
  if (withText !== source) return withText;
  const withGroup = source.replace(
    new RegExp(`(<g\\b[^>]*\\bid=["']${id}["'][^>]*>[\\s\\S]*?<(?:text|tspan)\\b[^>]*>)([\\s\\S]*?)(<\\/(?:text|tspan)>)`, 'i'),
    `$1${next}$3`
  );
  if (withGroup !== source) return withGroup;
  return source.replace(
    new RegExp(`<path\\b[^>]*\\bid=["']${id}["'][^>]*\\/?>`, 'i'),
    `<text id="${id}" class="white txt-main">${next}</text>`
  );
}

export function applyOverlayLabels(svgText: string, labels: { primaryLabel: string; secondaryLabel: string }) {
  const primary = normalizeOverlayLabel(labels.primaryLabel, DEFAULT_OVERLAY_PRIMARY_LABEL);
  const secondary = normalizeOverlayLabel(labels.secondaryLabel, DEFAULT_OVERLAY_SECONDARY_LABEL);
  return fitOverlayLabel(
    fitOverlayLabel(
      replaceLabeledText(
        replaceLabeledText(svgText, OVERLAY_PRIMARY_LABEL_ID, primary),
        OVERLAY_SECONDARY_LABEL_ID,
        secondary
      ),
      {
        id: OVERLAY_PRIMARY_LABEL_ID,
        cssClass: 'txt-main',
        defaultFontSize: 48,
        minFontSize: 18,
      }
    ),
    {
      id: OVERLAY_SECONDARY_LABEL_ID,
      cssClass: 'txt-sub',
      defaultFontSize: 24,
      minFontSize: 11,
    }
  );
}

export function measureOverlayTextWidth(text: string, fontSize: number, letterSpacing = 0) {
  const value = String(text || '');
  if (!value) return 0;
  if (typeof document !== 'undefined') {
    try {
      const ctx = document.createElement('canvas').getContext('2d');
      if (ctx?.measureText) {
        ctx.font = `700 ${fontSize}px Arial, Helvetica, sans-serif`;
        const spaced = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
        const supportsSpacing = typeof spaced.letterSpacing === 'string';
        if (supportsSpacing) spaced.letterSpacing = `${letterSpacing}px`;
        const width = ctx.measureText(value).width;
        if (width > 0) {
          return supportsSpacing ? width : width + letterSpacing * Math.max(0, value.length - 1);
        }
      }
    } catch {
      // jsdom and some workers have no canvas metrics
    }
  }
  return fontSize * 0.68 * value.length + letterSpacing * Math.max(0, value.length - 1);
}

function readCssPx(css: string, property: string, fallback: number) {
  const match = String(css || '').match(new RegExp(`${property}:\\s*([\\d.]+)px`, 'i'));
  const value = Number(match?.[1]);
  return Number.isFinite(value) ? value : fallback;
}

function setLabelFitStyle(svgText: string, id: string, fontSize: number, letterSpacing: number) {
  const style = `font-size:${Number(fontSize.toFixed(1))}px;letter-spacing:${Number(letterSpacing.toFixed(1))}px`;
  return String(svgText || '').replace(
    new RegExp(`(<(?:text|tspan)\\b[^>]*\\bid=["']${id}["'][^>]*)(\\/?>)`, 'i'),
    (full, start: string, end: string) => {
      if (/\sstyle=/i.test(start)) {
        return `${start.replace(/\sstyle="[^"]*"/i, ` style="${style}"`)}${end}`;
      }
      return `${start} style="${style}"${end}`;
    }
  );
}

export function fitOverlayLabel(svgText: string, args: {
  id: string;
  cssClass: string;
  defaultFontSize: number;
  minFontSize: number;
}) {
  const source = String(svgText || '');
  const tag = source.match(new RegExp(`<(?:text|tspan)\\b[^>]*\\bid=["']${args.id}["'][^>]*>`, 'i'))?.[0];
  if (!tag) return source;
  const label = String(source.match(new RegExp(`id=["']${args.id}["'][^>]*>([\\s\\S]*?)<\\/`, 'i'))?.[1] || '')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .trim();
  if (!label) return source;
  const x = Number(tag.match(/\bx="([\d.]+)"/i)?.[1]);
  const artboardWidth = svgArtboardSize(source).width;
  if (!Number.isFinite(x) || x <= 0) return source;
  const css = source.match(new RegExp(`\\.${args.cssClass}\\s*\\{([^}]+)\\}`, 'i'))?.[1] || '';
  const fontSize = readCssPx(css, 'font-size', args.defaultFontSize);
  const letterSpacing = readCssPx(css, 'letter-spacing', 0);
  const available = artboardWidth - x - 20;
  if (available <= 0) return source;
  const widthAt = (size: number, tracking: number) => measureOverlayTextWidth(label, size, tracking);
  if (widthAt(fontSize, letterSpacing) <= available) return source;
  let size = Math.max(args.minFontSize, fontSize * (available / Math.max(1, widthAt(fontSize, letterSpacing))));
  let tracking = Math.max(0, letterSpacing * (size / fontSize));
  while (size > args.minFontSize && widthAt(size, tracking) > available) {
    size -= 0.5;
    tracking = Math.max(0, letterSpacing * (size / fontSize));
  }
  if (widthAt(size, tracking) > available) tracking = 0;
  return setLabelFitStyle(source, args.id, size, tracking);
}

export function fitOverlaySecondaryLabel(svgText: string) {
  return fitOverlayLabel(svgText, {
    id: OVERLAY_SECONDARY_LABEL_ID,
    cssClass: 'txt-sub',
    defaultFontSize: 24,
    minFontSize: 11,
  });
}

export function placeOverlayInSlot(args: {
  slot: OverlaySlot;
  imageWidth: number;
  imageHeight: number;
  boxWidth: number;
  boxHeight: number;
  margin?: number;
}): OverlayBox {
  const margin = args.margin ?? Math.max(20, Math.round(Math.min(args.imageWidth, args.imageHeight) * 0.028));
  const maxWidth = Math.max(1, args.imageWidth - margin * 2);
  const maxHeight = Math.max(1, args.imageHeight - margin * 2);
  const scale = Math.min(1, maxWidth / args.boxWidth, maxHeight / args.boxHeight);
  const width = Math.max(1, Math.round(args.boxWidth * scale));
  const height = Math.max(1, Math.round(args.boxHeight * scale));
  const col = overlaySlotCol(args.slot);
  const row = overlaySlotRow(args.slot);
  const x =
    col === 'left'
      ? margin
      : col === 'right'
        ? args.imageWidth - margin - width
        : Math.round((args.imageWidth - width) / 2);
  const y =
    row === 'top'
      ? margin
      : row === 'bottom'
        ? args.imageHeight - margin - height
        : Math.round((args.imageHeight - height) / 2);
  return {
    x: clamp(x, margin, Math.max(margin, args.imageWidth - margin - width)),
    y: clamp(y, margin, Math.max(margin, args.imageHeight - margin - height)),
    width,
    height,
  };
}

function isSvgBrandLogo(brandLogo?: Blob | File | string) {
  if (brandLogo instanceof Blob) {
    const type = String(brandLogo.type || '').toLowerCase();
    if (type.includes('svg')) return true;
    return brandLogo instanceof File && /\.svg$/i.test(brandLogo.name);
  }
  return /\.svg(\?|#|$)/i.test(String(brandLogo || DEFAULT_BRAND_LOGO_SRC));
}

async function readBrandSource(brandLogo?: Blob | File | string) {
  if (brandLogo instanceof Blob) return brandLogo.text();
  const src = brandLogo || DEFAULT_BRAND_LOGO_SRC;
  const response = await fetch(src);
  if (!response.ok) throw new Error('Could not load the Keystone overlay SVG.');
  return response.text();
}

function svgArtboardSize(svgText: string) {
  const width = Number(String(svgText).match(/\bwidth="([\d.]+)"/i)?.[1]);
  const height = Number(String(svgText).match(/\bheight="([\d.]+)"/i)?.[1]);
  const viewBox = String(svgText).match(/viewBox="\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)"/i);
  return {
    width: width || Number(viewBox?.[1]) || 650,
    height: height || Number(viewBox?.[2]) || 150,
  };
}

function sizeSvgForOutput(svgText: string, width: number, height: number) {
  const sized = String(svgText || '')
    .replace(/\swidth="[^"]*"/i, ` width="${width}"`)
    .replace(/\sheight="[^"]*"/i, ` height="${height}"`);
  if (/<svg\b[^>]*\bwidth=/i.test(sized) && /<svg\b[^>]*\bheight=/i.test(sized)) return sized;
  return sized.replace(/<svg\b/i, `<svg width="${width}" height="${height}"`);
}

async function loadOverlayMark(args: {
  brandLogo?: Blob | File | string;
  primaryLabel: string;
  secondaryLabel: string;
  outputWidth?: number;
  outputHeight?: number;
}): Promise<{ image: HTMLImageElement; revoke?: () => void }> {
  if (isSvgBrandLogo(args.brandLogo)) {
    let svgText = applyOverlayLabels(await readBrandSource(args.brandLogo), {
      primaryLabel: args.primaryLabel,
      secondaryLabel: args.secondaryLabel,
    });
    if (args.outputWidth && args.outputHeight) {
      svgText = sizeSvgForOutput(svgText, args.outputWidth, args.outputHeight);
    }
    const blob = new Blob([svgText], { type: 'image/svg+xml' });
    const objectUrl = URL.createObjectURL(blob);
    const image = await loadImageElement(objectUrl);
    return { image, revoke: () => URL.revokeObjectURL(objectUrl) };
  }
  if (args.brandLogo instanceof Blob) {
    const objectUrl = URL.createObjectURL(args.brandLogo);
    const image = await loadImageElement(objectUrl);
    return { image, revoke: () => URL.revokeObjectURL(objectUrl) };
  }
  return { image: await loadImageElement(args.brandLogo || DEFAULT_BRAND_LOGO_SRC) };
}

export function overlayTargetBox(args: {
  imageWidth: number;
  imageHeight: number;
  artboardWidth: number;
  artboardHeight: number;
  margin?: number;
}) {
  const margin = args.margin ?? Math.max(20, Math.round(Math.min(args.imageWidth, args.imageHeight) * 0.028));
  const availW = Math.max(1, args.imageWidth - margin * 2);
  const availH = Math.max(1, args.imageHeight - margin * 2);
  const aspect = args.artboardWidth / Math.max(1, args.artboardHeight);
  const shortest = Math.min(args.imageWidth, args.imageHeight);
  let height = Math.max(args.artboardHeight, Math.round(shortest * 0.18));
  let width = Math.round(height * aspect);
  const minWidth = Math.round(args.imageWidth * 0.62);
  if (width < minWidth) {
    width = minWidth;
    height = Math.max(1, Math.round(width / aspect));
  }
  const fit = Math.min(1, availW / Math.max(1, width), availH / Math.max(1, height));
  return {
    width: Math.max(1, Math.round(width * fit)),
    height: Math.max(1, Math.round(height * fit)),
  };
}

export async function renderKeystoneOverlay(args: {
  source: Blob;
  primaryLabel: string;
  secondaryLabel: string;
  brandLogo?: Blob | File | string;
  position: OverlaySlot;
  originalFilename?: string;
  quality?: number;
}): Promise<ConvertedWebpImage & OverlayPlacement> {
  if (typeof document === 'undefined') {
    throw new Error('Image overlay is only available in the browser');
  }

  const primaryLabel = normalizeOverlayLabel(args.primaryLabel, DEFAULT_OVERLAY_PRIMARY_LABEL);
  const secondaryLabel = normalizeOverlayLabel(args.secondaryLabel, DEFAULT_OVERLAY_SECONDARY_LABEL);
  const position = isOverlaySlot(args.position) ? args.position : DEFAULT_OVERLAY_PLACEMENT.position;

  const sourceUrl = URL.createObjectURL(args.source);
  try {
    const photo = await loadImageElement(sourceUrl);
    const width = Math.max(1, photo.naturalWidth || photo.width || 1);
    const height = Math.max(1, photo.naturalHeight || photo.height || 1);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare the overlay canvas');

    ctx.drawImage(photo, 0, 0, width, height);

    const svgText = isSvgBrandLogo(args.brandLogo)
      ? await readBrandSource(args.brandLogo)
      : null;
    const artboard = svgText ? svgArtboardSize(svgText) : { width: 650, height: 150 };
    const target = overlayTargetBox({
      imageWidth: width,
      imageHeight: height,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });

    const { image: mark, revoke } = await loadOverlayMark({
      brandLogo: args.brandLogo,
      primaryLabel,
      secondaryLabel,
      outputWidth: svgText ? target.width * 2 : undefined,
      outputHeight: svgText ? target.height * 2 : undefined,
    });
    try {
      const naturalW = Math.max(1, mark.naturalWidth || mark.width || artboard.width);
      const naturalH = Math.max(1, mark.naturalHeight || mark.height || artboard.height);
      const box = placeOverlayInSlot({
        slot: position,
        imageWidth: width,
        imageHeight: height,
        boxWidth: svgText ? target.width : Math.round(target.height * (naturalW / naturalH)),
        boxHeight: svgText ? target.height : target.height,
      });
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(mark, box.x, box.y, box.width, box.height);
    } finally {
      revoke?.();
    }

    const blob = await canvasToBlob(canvas, 'image/webp', args.quality ?? 0.92);
    const baseName = slugifyOriginalName(args.originalFilename || 'image');
    return {
      blob,
      filename: toSeoWebpFilename(`${baseName}-keystone-overlay`, 'image'),
      width,
      height,
      position,
      primaryLabel,
      secondaryLabel,
    };
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

export async function createKeystoneOverlay(args: {
  source: Blob;
  primaryLabel: string;
  secondaryLabel: string;
  brandLogo?: Blob | File | string;
  position: OverlaySlot;
  originalFilename?: string;
}): Promise<ConvertedWebpImage & OverlayPlacement> {
  return renderKeystoneOverlay(args);
}
